import { bookingQueryKeys } from '@/hooks/queries/booking.queries';
import { FeedbackService } from '@/services/feedback.service';
import type { CreateBookingFeedbackRequestDto } from '@/types/dto/feedback.dto';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const feedbackQueryKey = (bookingId?: string | null) => ['booking-feedback', bookingId ?? null] as const;

export function useBookingFeedback(bookingId?: string | null, enabled = true) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: feedbackQueryKey(bookingId),
    enabled: enabled && Boolean(bookingId),
    queryFn: () => FeedbackService.getForBooking(bookingId!),
    retry: false,
  });
  const mutation = useMutation({
    mutationFn: (payload: CreateBookingFeedbackRequestDto) => FeedbackService.createForBooking(bookingId!, payload),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: feedbackQueryKey(bookingId) }),
        queryClient.invalidateQueries({ queryKey: bookingQueryKeys.all }),
      ]);
    },
  });

  return {
    context: query.data,
    isError: query.isError,
    isLoading: query.isLoading,
    refetch: query.refetch,
    submitFeedback: mutation.mutateAsync,
    isSubmitting: mutation.isPending,
  };
}
